// Manual playback of sound blocks.

import type CreativeEngine from "@cesdk/engine";
import type {DesignBlockId} from "@cesdk/cesdk-js";
import {BLOCK_TRIM_LENGTH_META_KEY, BLOCK_TRIM_OFFSET_META_KEY} from "../constants.ts";
import * as audioContext from "./audio-context.ts";
import {isMobile} from "../utils/browser.ts";
import {getAbsoluteStartOnPage, getDuration} from "../utils/slideUtilities.ts";

// testing parameters
const TEST_ALWAYS_USE:boolean = true;
const TEST_NO_MUTE_ORIGINAL:boolean = false;

// state
let willUse: boolean | undefined;   // cached value for shouldUse()

type SoundInfo = {
    id: DesignBlockId;

    // file
    fileUri: string,

    // playback properties
    start: number;
    end: number;
    speed: number;
    volume: number;
    trimOffset: number;
    trimLength: number;

    // load status
    loading: boolean;
    loaded: boolean;
    audioData: AudioBuffer | null;  // TODO: reuse if used by multiple sound blocks
    audioSourceNode: AudioBufferSourceNode | null;

    // playback status
    started: boolean;
    finished: boolean;
    paused: boolean;
};

let isPaused:boolean = false;
let currentSounds: SoundInfo[] = [];

export function isPlaying(): boolean {
    return currentSounds.some(s => s.started && !s.finished && !s.paused);
}

export function shouldUse(): boolean {
    if (willUse !== undefined)
        return willUse;

    if (isMobile() || !supportsWebCodecs()) {
        willUse = true;
        return true;
    }

    if (TEST_ALWAYS_USE) {
        willUse = true;
        return true;
    }

    willUse = false;
    return false;
}

function getSlideSounds(engine: CreativeEngine, pageId: DesignBlockId)
{
    // find all sounds
    const soundIds: DesignBlockId[] = getPageAudioBlocks(engine, pageId);

    const sounds: SoundInfo[] = soundIds.map((id) =>
    {
        const start = getAbsoluteStartOnPage(engine, id, pageId);
        const dur = getDuration(engine, id);

        let trimOffset = 0;
        let trimLength = 0;
        try {
            if (engine.block.hasMetadata(id, BLOCK_TRIM_OFFSET_META_KEY))
                trimOffset = Number(engine.block.getMetadata(id, BLOCK_TRIM_OFFSET_META_KEY));
            if (engine.block.hasMetadata(id, BLOCK_TRIM_LENGTH_META_KEY))
                trimLength = Number(engine.block.getMetadata(id, BLOCK_TRIM_LENGTH_META_KEY));
        }
        catch(err) {
            console.error("Error getting sound block metadata: ", err);
        }

        return {
            id: id,
            fileUri: engine.block.getString(id, "audio/fileURI"),
            start: start,
            end: start + dur,
            speed: engine.block.getPlaybackSpeed(id),
            volume: engine.block.getVolume(id),
            trimOffset: trimOffset,
            trimLength: trimLength,
            loading: false,
            loaded: false,
            audioData: null,
            audioSourceNode: null,
            started: false,
            finished: false,
            paused: false
        };
    }).sort((a, b) => a.start - b.start);

    // remove existing sounds from slide
    if (!TEST_NO_MUTE_ORIGINAL)
    {
        for (const id of soundIds) {
            engine.block.destroy(id);
        }
    }

    currentSounds = sounds;
    return currentSounds;
}

/**
 * Remove all sound blocks from a slide.
 * Used by the player for the first slide (to prevent audio from playing before the first interaction).
 */
export function removeSoundsFromSlide(engine: CreativeEngine, pageId: DesignBlockId) {
    // find all sound blocks
    const soundIds: DesignBlockId[] = getPageAudioBlocks(engine, pageId);

    for (const id of soundIds) {
        engine.block.destroy(id);
    }
}

/**
 * Preload a slide's sounds. Will not return until sounds are finished loading.
 * Will remove sound blocks from the page to prevent double-load.
 * Call when a slide is about to play (but before forceLoadResources() is called).
 */
export async function preloadSounds(engine: CreativeEngine, pageId: DesignBlockId): Promise<void>
{
    // clean-up any existing state (previous slide)
    unloadSounds();

    // build the sound map
    getSlideSounds(engine, pageId);

    if (!currentSounds.length)
        return;

    if (!audioContext.context) {
        console.error("Audio context not available, skipping sound preload");
        return;
    }

    for (const sound of currentSounds) {
        // start loading
        sound.loading = true;

        const res = await fetch(sound.fileUri);
        const arrayBuffer = await res.arrayBuffer();
        sound.audioData = await audioContext.context.decodeAudioData(arrayBuffer);

        sound.loading = false;
        sound.loaded = true;

    }
}

export function pause() {
    // prevent double call
    if (isPaused)
        return;

    // state
    isPaused = true;

    // pause any playing sounds
    for (const sound of currentSounds)
    {
        if (sound.started && !sound.finished)
        {
            if (sound.audioSourceNode) {
                try {
                    sound.audioSourceNode.stop();
                } catch (e) {
                    // ignore
                }
                sound.audioSourceNode = null;
            }
            sound.paused = true;
        }
    }
}

export function unPause() {
    // prevent double call
    if (!isPaused)
        return;

    // state
    isPaused = false;

    // don't continue if there's no audio context
    if (!audioContext.context)
        return;

    // resume context if we are becoming unpaused
    void audioContext.resume();

    // resume any previously paused sounds
    for (const sound of currentSounds)
    {
        if (sound.started && sound.paused) // don't test for finished here, will be set by the onended callback
        {
            // resume
            if (sound.paused) {
                void playSound(sound);
                sound.paused = false;
            }
        }
    }
}

/**
 * Start ticking for sound playback to track engine playback.
 */
export function startSoundPlayback(engine: CreativeEngine, pageId: DesignBlockId): (() => void) | null {
    engineForResume = engine;
    pageIdForResume = pageId;

    // check for no sounds, no need to start the tracker
    if (!currentSounds.length)
        return null;

    let raf: number = 0;
    let lastTime = -1;
    //const fired = new Set<Id>();

    const tick = () => {
        // get the current playback time
        const curTime = engine.block.supportsPlaybackTime(pageId) ? engine.block.getPlaybackTime(pageId) : 0;

        // If scrubbed backwards (or jumped), reset so hits can fire again.
        //if (lastTime >= 0 && curTime + 1e-6 < lastTime) {
        //    fired.clear();
        //}

        for (const s of currentSounds) {
            // play when crossing each sound start boundary
            if (lastTime < s.start && curTime >= s.start)
            {
                // start playing the sound
                void playSound(s);
            }
        }

        lastTime = curTime;
        raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    // return disposer
    return () => {
        cancelAnimationFrame(raf);
        engineForResume = null;
        pageIdForResume = null;
    };
}

function unloadSounds()
{
    for (const sound of currentSounds) {
        if (sound.audioSourceNode) {
            try {
                sound.audioSourceNode.stop();
            } catch (e) {
                // ignore
            }
        }
    }

    currentSounds = [];
}

async function playSound(sound: SoundInfo)
{
    // check for no audio
    if (sound.volume === 0) {
        return
    }

    // create audio context if doesn't exist, should be done on first interaction
    if (!audioContext.context) {
        console.error("No audio context");
        return;
    }

    // iOS may suspend until user gesture
    if (audioContext.context.state === 'suspended') {
        //await audioCtx.resume();
        console.warn("Audio context suspended");
    }

    // create source node
    const source = audioContext.context.createBufferSource();
    sound.audioSourceNode = source;
    source.buffer = sound.audioData;

    if (sound.volume !== 1) {
        const gainNode = audioContext.context.createGain();
        gainNode.gain.value = sound.volume;

        source.connect(gainNode);
        gainNode.connect(audioContext.context.destination);
    }
    else {
        source.connect(audioContext.context.destination);
    }

    // custom playback speed
    if (sound.speed !== 1)
        source.playbackRate.value = sound.speed;

    // track playback
    source.onended = () => {
        sound.finished = true;
        sound.audioSourceNode = null;   // free memory
    };

    // play
    sound.started = true;
    sound.paused = false;
    sound.finished = false;

    // calculate resume offset if it was paused
    let offset = sound.trimOffset ?? 0;
    if (engineForResume && pageIdForResume)
    {
        const curTime = engineForResume.block.supportsPlaybackTime(pageIdForResume) ? engineForResume.block.getPlaybackTime(pageIdForResume) : 0;

        // The sound starts at s.start on the page.
        // If curTime > s.start, we should start at (curTime - s.start) + s.trimOffset
        if (curTime > sound.start) {
            offset = (curTime - sound.start) + sound.trimOffset;
        }
    }

    console.log("XXX About to start sound: " + audioContext.context?.state);

    source.start(
        0,
        offset,                                              // offset into buffer (seconds)
        sound.trimLength ? sound.trimLength : undefined      // (optional) play only this long
    );
}

let engineForResume: CreativeEngine | null = null;
let pageIdForResume: DesignBlockId | null = null;

function supportsWebCodecs() {
    try {
        return !!(
            self.AudioDecoder
        );
    } catch {
        return false;
    }
}

function isDescendantOf(engine: CreativeEngine, node: DesignBlockId, ancestor: DesignBlockId): boolean {
    let cur: DesignBlockId | null = node;
    while (cur != null) {
        if (cur === ancestor)
            return true;
        cur = engine.block.getParent(cur);
    }
    return false;
}

function getPageAudioBlocks(engine: CreativeEngine, pageId: DesignBlockId): DesignBlockId[]
{
    const allAudio: DesignBlockId[] = engine.block.findByType("audio");
    return allAudio.filter((id: DesignBlockId) => isDescendantOf(engine, id, pageId));
}
